import { useEffect, useState } from 'react'
import { useServiceContext } from '~/root'
import { ApprovalValidationSchema } from './WFPhaseValidation'
import { useTranslation } from 'react-i18next'
import { transformFromServer, transformToServer } from '~/pages/settings/workflow-templates/DataService'
import { cloneDeep } from 'lodash'
import Modal from '../Modal'
import { FieldArray, Formik, yupToFormErrors } from 'formik'
import { Button, Form, FormCheck, FormGroup } from 'react-bootstrap'
import ApprovalEventActions, { ApprovalAction } from '~/pages/settings/workflow-templates/steps/ApprovalEventActions'
import UserGroupSteps, { UserGroupStep } from '~/pages/settings/workflow-templates/UserGroupSteps'
import { FsiType } from '~/constants/fsi'
import { getFileApproversV2, updateFileApproversV2 } from '~/services/api/files'
import { getAll } from '~/services/api/api.metadata'
import { WF_STEP_TYPE } from '~/types/workflow'
import Loader from '~/components/shared/Loader'
import { toast } from '~/services/toast'

interface ApprovalPhaseModalProps {
  fsi: any
  onClose?: () => void
}

interface WorkflowTemplate {
  id?: number
  name?: string
}

interface Approvers {
  type?: string
  steps?: UserGroupStep[]
  approveActions?: ApprovalAction[]
}

interface ApprovalPhaseFormValues {
  approvers: Approvers
  workflowTemplate?: WorkflowTemplate
  readOnly?: boolean
}

function ApprovalPhaseModal({ fsi, onClose }: ApprovalPhaseModalProps) {
  const isFromManageWFModals = true
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [formData, setFormData] = useState<ApprovalPhaseFormValues>({
    approvers: {
      steps: [],
      approveActions: []
    }
  })
  const approvalValidationSchema = ApprovalValidationSchema(translate)

  useEffect(() => {
    if (fsi?.id) {
      setIsLoading(true)
      getFileApproversV2(fsi.id).then(function (approvers) {
        getAll({ sortBy: 'NAME', sortDesc: false }).then((meta) => {
          // Support both Restangular and plain object responses
          const approversData = (approvers as any).plain ? (approvers as any).plain() : approvers
          const metaData = (meta as any).plain ? (meta as any).plain() : meta
          const transformed = transformFromServer(approversData, metaData.elements)
          if (transformed?.approvers?.approveActions?.length === 0) {
            transformed.approvers.approveActions = [
              {
                actionType: '',
                actionData: '',
                usersGroups: []
              }
            ]
          }
          setFormData(transformed)
          setIsLoading(false)
        })
      })
    }
  }, [fsi])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  function doSave(values: ApprovalPhaseFormValues) {
    const cloneVals = cloneDeep(values)
    const saveRecursivelyVar = (cloneVals as any).saveRecursivelyVar
    delete (cloneVals as any).saveRecursivelyVar
    const finalData = transformToServer(cloneVals)
    const parsedApproveActions = finalData?.approvers?.approveActions?.reduce(
      (acc: ApprovalAction[], item: ApprovalAction) => {
        if (item?.actionType || (item as any)['@type']) {
          acc.push(item)
        }
        return acc
      },
      []
    )
    finalData.approvers.approveActions = parsedApproveActions
    updateFileApproversV2(fsi.id, finalData, false, saveRecursivelyVar).then(
      () => {
        doClose()
      },
      (data: any) => {
        toast.error(data?.data?.message ? data.data.message : translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
      }
    )
  }

  return (
    <Modal size="lg" show={isOpen} title={translate('BROWSER_FILE_SETTINGS_APPROVERS_H_TITLE')} handleClose={doClose}>
      <Formik
        innerRef={(el) => {
          el ? (context.store.formRef = el) : null
        }}
        initialValues={{
          ...cloneDeep(formData)
        }}
        enableReinitialize
        validate={(values: ApprovalPhaseFormValues) => {
          return approvalValidationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => {
                return yupToFormErrors(err)
              }
            )
        }}
        onSubmit={(values: ApprovalPhaseFormValues) => {
          doSave(values)
        }}
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <>
            <Loader blocking={isLoading}>
              <Modal.Body>
                <div className="form-group md-form-group w100p">
                  <label>{fsi?.type === FsiType.FOLDER ? translate('FSI_TYPE_FOLDER') : translate('DOCUMENT')}</label>
                  {fsi?.name ? <p className="whitespaceprewrap fs12">{fsi.name}</p> : null}
                </div>
                {values?.workflowTemplate?.id && (
                  <div className="form-group md-form-group w100p">
                    <label>{translate('WF_TEMPLATE_LABEL')}</label>
                    <p>{values.workflowTemplate?.name || '-'}</p>
                  </div>
                )}
                <FormGroup>
                  <Form.Label>{translate('BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_TYPE_HEADLINE')}</Form.Label>
                  <FormCheck
                    name="approvers.type"
                    value={WF_STEP_TYPE.SETUP_WITH_WORKFLOW}
                    checked={values?.approvers?.type === WF_STEP_TYPE.SETUP_WITH_WORKFLOW}
                    type="radio"
                    id={`98960996-3726-4cc6-b695-8b59ca85c8ab-${WF_STEP_TYPE.SETUP_WITH_WORKFLOW}`}
                    disabled={values?.readOnly}
                    label={
                      <div className="px-2 font-weight-normal">
                        {translate('BROWSER_FILE_SETTINGS_APPROVERS_LABEL_SETUP_APPROVERS_WITH_CR')}
                      </div>
                    }
                    onChange={() => setFieldValue('approvers.type', WF_STEP_TYPE.SETUP_WITH_WORKFLOW)}
                  />

                  <FormCheck
                    name="approvers.type"
                    value={WF_STEP_TYPE.USE_PREDEFINED}
                    checked={values?.approvers?.type === WF_STEP_TYPE.USE_PREDEFINED}
                    type={'radio'}
                    id={`98960996-3726-4cc6-b695-8b59ca85c8ab-${WF_STEP_TYPE.USE_PREDEFINED}`}
                    disabled={values?.readOnly}
                    label={
                      <div className="px-2 font-weight-normal">
                        {translate('BROWSER_FILE_SETTINGS_APPROVERS_LABEL_USE_PREDEFINED')}
                      </div>
                    }
                    onChange={() => setFieldValue('approvers.type', WF_STEP_TYPE.USE_PREDEFINED)}
                  />

                  <FormCheck
                    name="approvers.type"
                    value={WF_STEP_TYPE.NOT_DEFINED}
                    checked={values?.approvers?.type === WF_STEP_TYPE.NOT_DEFINED}
                    type={'radio'}
                    id={`98960996-3726-4cc6-b695-8b59ca85c8ab-${WF_STEP_TYPE.NOT_DEFINED}`}
                    disabled={values?.readOnly}
                    label={
                      <div className="px-2 font-weight-normal">
                        {translate('BROWSER_FILE_SETTINGS_APPROVERS_LABEL_NOT_DEFINED')}
                      </div>
                    }
                    onChange={() => setFieldValue('approvers.type', WF_STEP_TYPE.NOT_DEFINED)}
                  />
                </FormGroup>

                {values?.approvers?.type === WF_STEP_TYPE.USE_PREDEFINED ? (
                  <FieldArray name="approvers.steps">
                    {(arrayHelpers) => (
                      <UserGroupSteps
                        arrayHelpers={arrayHelpers}
                        values={{ steps: values?.approvers?.steps || [] }}
                        nofallTxt={'BROWSER_FILE_SETTINGS_APPROVERS_INFO_LABEL'}
                        labelTxt={'BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_SEQUENCE_HEADLINE'}
                        isFromManageWFModals={isFromManageWFModals}
                        disabled={values?.readOnly}
                      />
                    )}
                  </FieldArray>
                ) : null}

                <FormGroup>
                  <Form.Label>{translate('BROWSER_FILE_SETTINGS_APPROVERS_APPROVAL_EVENTS_HEADLINE')}</Form.Label>

                  <FieldArray name="approvers.approveActions">
                    {(arrayHelpers) => (
                      <ApprovalEventActions
                        arrayHelpers={arrayHelpers}
                        values={{ approveActions: values?.approvers?.approveActions || [] }}
                        isFromManageWFModals={isFromManageWFModals}
                        isDisabled={values?.readOnly}
                      />
                    )}
                  </FieldArray>
                </FormGroup>
              </Modal.Body>
            </Loader>
            <Modal.Footer>
              {!!values?.readOnly && (
                <Button className="btn btn-primary at_btnSaveApprovers" type="button" onClick={doClose}>
                  {translate('OK')}
                </Button>
              )}
              {!values?.readOnly && (
                <>
                  <Button className="btn btn-close" variant="link" type="button" onClick={doClose}>
                    {translate('CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn-primary at_btnSaveApprovers"
                    onClick={() => {
                      setFieldValue('saveRecursivelyVar', false)
                      handleSubmit()
                    }}
                  >
                    {translate('SAVE_BTN')}
                  </Button>
                </>
              )}
              {fsi?.type === context.constant.file.type.FOLDER && !values?.readOnly && (
                <Button
                  type="submit"
                  className="btn btn-primary at_btnSaveAclsRecursively"
                  onClick={() => {
                    setFieldValue('saveRecursivelyVar', true)
                    handleSubmit()
                  }}
                >
                  {translate('SAVE_FOR_ENCLOSED_ITEMS')}
                </Button>
              )}
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default ApprovalPhaseModal
